import { Action, Dispatch } from 'redux';
import moment from 'moment';
import _ from 'lodash';
import { IAppState } from '../reducers';
import { getLivestreamJobs } from '../../api/vault/livestream/api';
import { StreamStatus } from '../../enums';
import { ILiveStreamJob } from '../../api/vault/livestream';
import { JobTab, Order } from '../jobStatus';
import { isArrayEqual } from '../../utils';

// Stream Statuses
// An event waiting for its feed or being wrapped up after losing it is every bit
// as active as one that is Live - it has a host running and needs watching.
const activeStreamStatuses = [
  StreamStatus.Live, StreamStatus.Initializing, StreamStatus.Processing, StreamStatus.Restarting,
  StreamStatus.AwaitingSource, StreamStatus.SourceEnded,
];
const failedStreamStatuses = [StreamStatus.Error];

export interface IJobLiveEvent {
  id: string;
  name: string;
  start: string;
  end: string;
  streamStatus: StreamStatus;
  owner: string;
  duration: string;
}

export interface ILiveEventsTab {
  liveEvents: ILiveStreamJob[];
  order: Order;
  orderBy: string;
  statusFilter: string;
  failed: ILiveStreamJob[];
  active: ILiveStreamJob[];
}

export interface IState {
  liveEvents: ILiveStreamJob[];
  vault: ILiveEventsTab;
  exchange: ILiveEventsTab;
  isLoading: boolean;
}

export enum ActionType {
  StartLoading = 'JobsLiveEvents.StartLoading',
  FinishLoading = 'JobsLiveEvents.FinishLoading',
  UpdateLiveEvents = 'JobsLiveEvents.UpdateLiveEvents',
  UpdateVaultStatusFilter = 'JobsLiveEvents.UpdateVaultStatusFilter',
  UpdateVaultSortingOrder = 'JobsLiveEvents.UpdateVaultSortingOrder',
  UpdateExchangeStatusFilter = 'JobsLiveEvents.UpdateExchangeStatusFilter',
  UpdateExchangeSortingOrder = 'JobsLiveEvents.UpdateExchangeSortingOrder',
}

export interface IJobsLiveEventsAction extends Action<ActionType> {
  payload?: any;
}

const INITIAL_STATE: IState = {
  liveEvents: [],
  isLoading: false,
  vault: {
    liveEvents: [],
    statusFilter: 'All',
    order: 'asc',
    orderBy: 'start',
    active: [],
    failed: [],
  },
  exchange: {
    liveEvents: [],
    statusFilter: 'All',
    order: 'asc',
    orderBy: 'start',
    active: [],
    failed: [],
  },
};

export default function reducer(state: IState = INITIAL_STATE, action: IJobsLiveEventsAction): IState {
  switch (action.type) {
    case ActionType.StartLoading:
      return {
        ...state,
        isLoading: true,
      };
    case ActionType.FinishLoading:
      return {
        ...state,
        isLoading: false,
      };
    case ActionType.UpdateLiveEvents:
      return {
        ...state,
        liveEvents: action.payload.liveEvents,
        vault: action.payload.vault,
        exchange: action.payload.exchange,
      };
    case ActionType.UpdateVaultStatusFilter:
      return {
        ...state,
        vault: {
          ...state.vault,
          statusFilter: action.payload.statusFilter,
          liveEvents: action.payload.liveEvents,
        },
      };
    case ActionType.UpdateVaultSortingOrder:
      return {
        ...state,
        vault: {
          ...state.vault,
          order: action.payload.order,
          orderBy: action.payload.orderBy,
          liveEvents: action.payload.liveEvents,
        },
      };
    case ActionType.UpdateExchangeStatusFilter:
      return {
        ...state,
        exchange: {
          ...state.exchange,
          statusFilter: action.payload.statusFilter,
          liveEvents: action.payload.liveEvents,
        },
      };
    case ActionType.UpdateExchangeSortingOrder:
      return {
        ...state,
        exchange: {
          ...state.exchange,
          order: action.payload.order,
          orderBy: action.payload.orderBy,
          liveEvents: action.payload.liveEvents,
        },
      };
    default:
      return state;
  }
}

const getActiveAndFailedLiveStreams = (jobs: ILiveStreamJob[]) => {
  const getLiveEvents = (liveEvents: ILiveStreamJob[], streamStatus: StreamStatus[]): ILiveStreamJob[] => (
    liveEvents ? liveEvents.filter((le) => streamStatus.includes(le._source.streamStatus)) : []
  );

  return {
    activeLiveStreams: getLiveEvents(jobs, activeStreamStatuses),
    failedLiveStreams: getLiveEvents(jobs, failedStreamStatuses),
  };
};

const getFilteredAndSortedJobs = (jobs: ILiveStreamJob[], statusFilter: string, orderBy: string, order: Order) => {
  let filteredJobs = jobs;
  if (statusFilter !== 'All') filteredJobs = filteredJobs.filter(({ _source: { streamStatus } }) => streamStatus === statusFilter);
  // Sort
  let sortedJobs;
  if (orderBy === 'eventTitle' || orderBy === 'streamStatus') {
    const getValue = (job: ILiveStreamJob) => {
      if (orderBy === 'streamStatus') return job._source.streamStatus;
      return job._source.title;
    };
    sortedJobs = filteredJobs.sort((a, b) => {
      const fieldA = getValue(a);
      const fieldB = getValue(b);
      return order === 'asc' ? fieldA.localeCompare(fieldB) : fieldB.localeCompare(fieldA);
    });
  } else if (orderBy === 'start') {
    sortedJobs = _.orderBy<ILiveStreamJob>(filteredJobs, (job) => [moment(job._source.start).unix()], [order]);
  } else if (orderBy === 'duration') {
    sortedJobs = filteredJobs.sort((a, b) => (a.duration - b.duration) * (order === 'asc' ? 1 : -1));
  }
  return sortedJobs;
};

export interface IFetchLiveEventsJobsParams {
  date?: Date; // If empty it will fetch today's live events
}

const getLiveAndExchangeEvents = (events: ILiveStreamJob[]) => {
  const exchangeLiveEvents = events.filter((le) => le._source.exchangeLiveStreamScheduleId);
  const vaultLiveEvents = events.filter((le) => !le._source.exchangeLiveStreamScheduleId);
  return { exchangeLiveEvents, vaultLiveEvents };
};

export const fetchLiveEvents = (params: IFetchLiveEventsJobsParams) => async (dispatch: Dispatch, getState: () => IAppState) => {
  const {
    jobsLiveEvents: {
      isLoading, liveEvents: oldLiveEvents, vault, exchange,
    },
  } = getState();
  if (isLoading) return; // Do not fetch if already loading
  dispatch({ type: ActionType.StartLoading });

  try {
    const res = await getLivestreamJobs(params.date);
    // Check diff
    const diff = !isArrayEqual(oldLiveEvents, res.data.data);
    if (diff) {
      const newLiveEvents = res.data.data;
      const { exchangeLiveEvents, vaultLiveEvents } = getLiveAndExchangeEvents(newLiveEvents);
      const { activeLiveStreams, failedLiveStreams } = getActiveAndFailedLiveStreams(vaultLiveEvents);
      const { activeLiveStreams: activeExchangeLiveStreams, failedLiveStreams: failedExchangeLiveStreams } = getActiveAndFailedLiveStreams(exchangeLiveEvents);
      dispatch({
        type: ActionType.UpdateLiveEvents,
        payload: {
          liveEvents: newLiveEvents,
          vault: {
            ...vault,
            liveEvents: getFilteredAndSortedJobs(vaultLiveEvents, vault.statusFilter, vault.orderBy, vault.order),
            active: activeLiveStreams,
            failed: failedLiveStreams,
          },
          exchange: {
            ...exchange,
            liveEvents: getFilteredAndSortedJobs(exchangeLiveEvents, exchange.statusFilter, exchange.orderBy, exchange.order),
            active: activeExchangeLiveStreams,
            failed: failedExchangeLiveStreams,
          },
        },
      });
    }
  } catch (error) {
    console.error('[QA] Error fetching live events:', error);
  } finally {
    dispatch({
      type: ActionType.FinishLoading,
    });
  }
};

export const selectFailedLiveEvents = (state: IAppState) => state.jobsLiveEvents?.liveEvents?.filter((e) => e._source.streamStatus === StreamStatus.Error) ?? [];

export const updateStatusFilter = (statusFilter: string) => (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const { jobsLiveEvents: { liveEvents }, jobStatus: { jobTab } } = getState();
  const { exchangeLiveEvents, vaultLiveEvents } = getLiveAndExchangeEvents(liveEvents);
  if (jobTab === JobTab.ExchangeEvents) {
    dispatch({
      type: ActionType.UpdateExchangeStatusFilter,
      payload: {
        statusFilter,
        liveEvents: getFilteredAndSortedJobs(exchangeLiveEvents, statusFilter, 'start', 'desc'),
      },
    });
  } else if (jobTab === JobTab.LiveEvents) {
    dispatch({
      type: ActionType.UpdateVaultStatusFilter,
      payload: {
        statusFilter,
        liveEvents: getFilteredAndSortedJobs(vaultLiveEvents, statusFilter, 'start', 'desc'),
      },
    });
  }
};

export const updateSortingOrder = (orderBy: string, order: Order) => (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const { jobsLiveEvents: { exchange, vault }, jobStatus: { jobTab } } = getState();
  if (jobTab === JobTab.ExchangeEvents) {
    dispatch({
      type: ActionType.UpdateExchangeSortingOrder,
      payload: {
        order,
        orderBy,
        liveEvents: getFilteredAndSortedJobs(exchange.liveEvents, exchange.statusFilter, orderBy, order),
      },
    });
  } else if (jobTab === JobTab.LiveEvents) {
    dispatch({
      type: ActionType.UpdateVaultSortingOrder,
      payload: {
        order,
        orderBy,
        liveEvents: getFilteredAndSortedJobs(vault.liveEvents, vault.statusFilter, orderBy, order),
      },
    });
  }
};
